import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { Route } from "./+types/_index";
import { Container } from "~/components/layout/Container";
import { Eyebrow } from "~/components/common/Eyebrow";
import { ButtonLink } from "~/components/common/Button";
import { HomeHero } from "~/components/home/HomeHero";
import { MerchantCard } from "~/components/merchant/MerchantCard";
import {
  ActivityIcon,
  ArrowRightIcon,
  BuildingIcon,
  LockIcon,
  ShieldCheckIcon,
} from "~/components/common/icons";
import { getMerchants } from "~/lib/api/merchants.server";
import { getServiceItems } from "~/lib/api/services.server";
import { getMerchantVerification } from "~/lib/data/merchant";
import { safe } from "~/lib/safe";
import { cacheHeaders } from "~/lib/cache-headers";

export async function loader() {
  const [merchants, serviceItems] = await Promise.all([
    safe(() => getMerchants(), [] as Awaited<ReturnType<typeof getMerchants>>),
    safe(
      () => getServiceItems(),
      [] as Awaited<ReturnType<typeof getServiceItems>>,
    ),
  ]);

  const stats = {
    verifiedEntities: merchants.filter(
      m => getMerchantVerification(m) === "verified",
    ).length,
    totalMerchants: merchants.length,
    activeServices: serviceItems.filter(i => i.item_type === "SERVICE").length,
  };

  const featured = merchants
    .map(m => ({
      id: String(m.id),
      name: (m.merchant_name as string | null) ?? null,
      commonName: (m.merchant_common_name as string | null) ?? null,
      description: (m.merchant_description as string | null) ?? null,
      logoUrl: (m.merchant_image_url_logo as string | null) ?? null,
      verification: getMerchantVerification(m),
    }))
    .slice(0, 6);

  return { stats, featured };
}

export const headers = () => cacheHeaders["public-300"];

export const meta: Route.MetaFunction = () => [
  { title: "Raytio Trust Marketplace" },
  {
    name: "description",
    content:
      "Discover verified merchants and trusted service providers on the Raytio Trust Marketplace.",
  },
];

export default function HomePage({ loaderData }: Route.ComponentProps) {
  const { stats, featured } = loaderData;
  const { t } = useTranslation();

  const statItems = [
    {
      value: stats.verifiedEntities,
      label: t("HomePage.stat-verified-entities"),
      icon: <ShieldCheckIcon className="h-5 w-5" />,
      color: "text-raytio-viola-text dark:text-raytio-viola",
      tile: "bg-raytio-viola/15 text-raytio-viola-text dark:text-raytio-viola",
    },
    {
      value: stats.totalMerchants,
      label: t("HomePage.stat-total-merchants"),
      icon: <BuildingIcon className="h-5 w-5" />,
      color: "text-raytio-poolblue-text dark:text-raytio-poolblue",
      tile: "bg-raytio-poolblue/15 text-raytio-poolblue-text dark:text-raytio-poolblue",
    },
    {
      value: stats.activeServices,
      label: t("HomePage.stat-active-services"),
      icon: <ActivityIcon className="h-5 w-5" />,
      color: "text-raytio-green",
      tile: "bg-raytio-green/15 text-raytio-green",
    },
  ];

  const features = [
    {
      icon: <LockIcon className="h-6 w-6" />,
      title: t("HomePage.feature-1-title"),
      description: t("HomePage.feature-1-desc"),
    },
    {
      icon: <BuildingIcon className="h-6 w-6" />,
      title: t("HomePage.feature-2-title"),
      description: t("HomePage.feature-2-desc"),
    },
    {
      icon: <ShieldCheckIcon className="h-6 w-6" />,
      title: t("HomePage.feature-3-title"),
      description: t("HomePage.feature-3-desc"),
    },
  ];

  return (
    <>
      <HomeHero />

      {/* Stats */}
      <section className="pb-28">
        <Container>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            {statItems.map(stat => (
              <div
                key={stat.label}
                className="rounded-xl border border-border-shell/60 bg-surface-blank p-6 shadow-simple dark:border-transparent"
              >
                <span
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${stat.tile}`}
                >
                  {stat.icon}
                </span>
                <div className="mt-4 font-heading text-3xl font-extrabold text-text-full">
                  {stat.value}
                </div>
                <div
                  className={`mt-1 font-heading text-xs font-bold uppercase tracking-[0.12em] ${stat.color}`}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Featured / Verified Merchants */}
      {featured.length > 0 && (
        <section className="pb-20">
          <Container>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="font-heading text-3xl font-bold text-text-full">
                  {t("HomePage.verified-merchants")}
                </h2>
                <p className="mt-2 text-text-medium">
                  {t("HomePage.featured-subtitle")}
                </p>
              </div>
              <Link
                to="/merchants"
                className="inline-flex items-center gap-1 font-heading text-sm font-semibold uppercase tracking-wider text-accent hover:underline"
              >
                {t("HomePage.view-all")}
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map(merchant => (
                <MerchantCard key={merchant.id} {...merchant} />
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* How it works — mirrors the About page's verification content */}
      <section className="bg-ink py-24">
        <Container>
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>{t("HomePage.how-eyebrow")}</Eyebrow>
            <h2 className="mt-3 font-heading text-3xl font-bold text-white sm:text-4xl">
              {t("HomePage.how-title")}
            </h2>
            <p className="mt-4 text-white/60">{t("HomePage.how-subtitle")}</p>
          </div>
          <div className="mx-auto mt-16 grid max-w-5xl gap-10 md:grid-cols-3">
            {features.map(feature => (
              <div key={feature.title}>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent/15 text-accent">
                  {feature.icon}
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-white">
                  {feature.title}
                </h3>
                <div className="mt-3 border-t border-dashed border-white/15" />
                <p className="mt-3 text-sm leading-relaxed text-white/60">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-20">
        <Container>
          <div className="relative mx-auto max-w-3xl overflow-hidden rounded-2xl border border-border-shell/60 bg-surface-blank p-10 text-center shadow-content dark:border-transparent sm:p-14">
            <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-accent via-raytio-poolblue to-raytio-viola" />
            <h2 className="font-heading text-3xl font-bold text-text-full">
              {t("HomePage.cta-title")}
            </h2>
            <p className="mt-3 text-text-medium">
              {t("HomePage.cta-subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <ButtonLink to="/merchants" variant="primary">
                {t("HomePage.cta-browse")}
              </ButtonLink>
              <ButtonLink to="/services" variant="outline">
                {t("HomePage.cta-explore")}
              </ButtonLink>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
